import React, { useState, useEffect } from 'react';
import { Upload, Button, Select, InputNumber, Input, message, Card, Row, Col, Typography, Table, Tag, Space, Divider, Progress, Modal, Popconfirm } from 'antd';
import { UploadOutlined, FileExcelOutlined, CheckCircleOutlined, CloseCircleOutlined, WarningOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../utils/api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

export default function ExcelUpload() {
  const [companies, setCompanies] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(0);

  // Form state
  const [uploadType, setUploadType] = useState('youtube_revenue');
  const [companyId, setCompanyId] = useState(null);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [file, setFile] = useState(null);
  const [formats, setFormats] = useState([]);
  const [formatModal, setFormatModal] = useState(false);
  const [newFormatName, setNewFormatName] = useState('');
  const [newFormatColumns, setNewFormatColumns] = useState('');

  useEffect(() => { fetchCompanies(); fetchUploads(); fetchFormats(); }, []);

  const fetchFormats = async () => {
    try { const res = await api.get('/excel/formats'); setFormats(res.data); } catch (err) { console.error(err); }
  };

  const handleAddFormat = async () => {
    if (!newFormatName.trim() || !newFormatColumns.trim()) { message.error('Name and columns required'); return; }
    try {
      await api.post('/excel/formats', { name: newFormatName, columns_list: newFormatColumns });
      message.success('Format added!');
      setFormatModal(false); setNewFormatName(''); setNewFormatColumns('');
      fetchFormats();
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const handleDeleteFormat = async (id) => {
    try {
      await api.delete(`/excel/formats/${id}`);
      message.success('Format deleted');
      fetchFormats();
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const fetchCompanies = async () => {
    try { const res = await api.get('/companies'); setCompanies(res.data); } catch (err) { console.error(err); }
  };

  const fetchUploads = async () => {
    try { const res = await api.get('/excel/uploads'); setUploads(res.data); } catch (err) { console.error(err); }
  };

  // Build the multipart payload for an upload. `mode` is only used for song_data.
  const buildForm = (mode) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('company_id', companyId);
    if (uploadType !== 'song_data') {
      formData.append('month', month);
      formData.append('year', year);
    }
    if (uploadType === 'song_data' && mode) formData.append('mode', mode);
    return formData;
  };

  // Actually commit a song_data import in the chosen mode (update existing / skip dups).
  const commitSongImport = async (mode) => {
    setLoading(true);
    setProgress(0);
    try {
      const res = await api.post('/excel/song-data', buildForm(mode), {
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded * 100) / e.total)); }
      });
      setProgress(100);
      setResult(res.data);
      message.success(res.data.message || 'Import complete');
      fetchUploads();
      setFile(null);
    } catch (err) {
      message.error(err.response?.data?.error || 'Upload failed');
    }
    setLoading(false);
  };

  const handleUpload = async () => {
    if (!file || !companyId) {
      message.error('File and Company are required');
      return;
    }

    // Custom format upload
    if (uploadType.startsWith('custom_')) {
      const formatId = parseInt(uploadType.replace('custom_', ''));
      setLoading(true); setProgress(0);
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('format_id', formatId);
        formData.append('company_id', companyId);
        formData.append('month', month);
        formData.append('year', year);
        const res = await api.post('/excel/custom-upload', formData, {
          onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded * 100) / e.total)); }
        });
        setProgress(100);
        setResult({ total: res.data.total, matched: res.data.total, unmatched: 0, message: res.data.message });
        message.success(res.data.message || 'Data imported!');
        fetchUploads(); setFile(null);
      } catch (err) { message.error(err.response?.data?.error || 'Upload failed'); }
      setLoading(false);
      return;
    }

    // Song/Distribution data: first do a dry-run preview to detect duplicates
    // (same UPC / title already in the system) and let the admin decide.
    if (uploadType === 'song_data') {
      setLoading(true);
      setProgress(0);
      try {
        const res = await api.post('/excel/song-data', buildForm('preview'), {
          onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded * 100) / e.total)); }
        });
        setProgress(100);
        setLoading(false);
        const { new_count = 0, duplicate_count = 0, duplicates = [] } = res.data || {};
        if (duplicate_count === 0) {
          await commitSongImport('skip'); // nothing to confirm — just import new ones
          return;
        }
        const dupList = duplicates.slice(0, 30).map((d, i) =>
          `${i + 1}. ${d.title || d.existing_title || '(no title)'}${d.upc ? ` — UPC ${d.upc}` : ''}${d.isrc ? ` — ISRC ${d.isrc}` : ''}`
        ).join('\n');
        Modal.confirm({
          title: <span><WarningOutlined style={{ color: '#faad14' }} /> Some songs already exist</span>,
          width: 640,
          icon: null,
          content: (
            <div>
              <p><b>{new_count}</b> new songs will be added. <b>{duplicate_count}</b> already exist in the system (matched by UPC/title).</p>
              <p>Do you want to <b>update</b> their details, or <b>skip</b> them (add only the new ones)?</p>
              <div style={{ maxHeight: 220, overflow: 'auto', background: '#fafafa', border: '1px solid #eee', borderRadius: 6, padding: 8, fontSize: 12, whiteSpace: 'pre-wrap' }}>{dupList}{duplicate_count > 30 ? `\n…and ${duplicate_count - 30} more` : ''}</div>
            </div>
          ),
          okText: 'Update existing',
          cancelText: 'Skip (add new only)',
          okButtonProps: { type: 'primary' },
          // antd only has OK/Cancel; map OK->update, Cancel->skip. A separate close = no action.
          onOk: () => commitSongImport('update'),
          onCancel: () => commitSongImport('skip'),
        });
      } catch (err) {
        setLoading(false);
        message.error(err.response?.data?.error || 'Upload failed');
      }
      return;
    }

    setLoading(true);
    setProgress(0);
    try {
      const endpoint = `/excel/${uploadType.replace('_', '-')}`;
      const res = await api.post(endpoint, buildForm(), {
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        }
      });
      setProgress(100);
      setResult(res.data);
      message.success(res.data.message);
      fetchUploads();
      setFile(null);
    } catch (err) {
      message.error(err.response?.data?.error || 'Upload failed');
    }
    setLoading(false);
  };

  const uploadTypeLabels = {
    youtube_revenue: 'YouTube Revenue',
    distribution_royalty: 'Distribution Royalty',
    channel_revenue: 'Channel Revenue',
    expense_data: 'Expense Data',
    song_data: 'Song Data'
  };

  const handleDeleteUpload = async (id) => {
    try {
      const res = await api.delete(`/excel/uploads/${id}`);
      message.success(res.data?.message || 'Upload deleted');
      fetchUploads();
    } catch (err) { message.error(err.response?.data?.error || 'Delete failed'); }
  };

  const uploadColumns = [
    { title: 'File', dataIndex: 'file_name', key: 'file' },
    { title: 'Type', dataIndex: 'upload_type', key: 'type', render: (v) => uploadTypeLabels[v] || v },
    { title: 'Company', dataIndex: 'company_name', key: 'company' },
    { title: 'Month/Year', key: 'period', render: (_, r) => r.month ? `${r.month}/${r.year}` : '-' },
    { title: 'Added', key: 'added', render: (_, r) => (
      <Space size={4} wrap>
        {Number(r.songs_added) > 0 && <Tag color="blue">{r.songs_added} songs</Tag>}
        {Number(r.income_added) > 0 && <Tag color="green">{r.income_added} income</Tag>}
        {Number(r.expenses_added) > 0 && <Tag color="gold">{r.expenses_added} expense</Tag>}
        {Number(r.rows_updated) > 0 && <Tag color="purple">{r.rows_updated} updated</Tag>}
        {!Number(r.songs_added) && !Number(r.income_added) && !Number(r.expenses_added) && !Number(r.rows_updated) && <Text type="secondary">-</Text>}
      </Space>
    ) },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => <Tag color={s === 'completed' ? 'green' : s === 'failed' ? 'red' : 'blue'}>{s}</Tag> },
    { title: 'Date', dataIndex: 'created_at', key: 'date', render: (v) => new Date(v).toLocaleDateString('en-IN') },
    { title: 'Action', key: 'action', render: (_, r) => (
      <Popconfirm
        title="Delete this upload?"
        description="All data (songs/income/expense) added by this Excel will be removed. Songs that this upload had updated will be restored to their previous values. Are you sure?"
        okText="Yes, delete" cancelText="No" okButtonProps={{ danger: true }}
        onConfirm={() => handleDeleteUpload(r.id)}>
        <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
      </Popconfirm>
    ) }
  ];

  return (
    <div>
      <Title level={3}><FileExcelOutlined style={{ color: '#217346' }} /> Excel Upload</Title>
      <Paragraph type="secondary">
        Upload Excel/CSV files to automatically import data. The system will match songs by Title, ISRC, UPC and channels by name.
      </Paragraph>

      <Row gutter={24}>
        <Col xs={24} lg={12}>
          <Card title="Upload New File" style={{ borderRadius: 12 }}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong>Upload Type</Text>
                <Select value={uploadType} onChange={setUploadType} style={{ width: '100%', marginTop: 4 }}>
                  <Option value="youtube_revenue">YouTube Revenue</Option>
                  <Option value="distribution_royalty">Distribution Royalty</Option>
                  <Option value="channel_revenue">Channel Revenue (Auto-sync to Staff)</Option>
                  <Option value="expense_data">Expense Data</Option>
                  <Option value="song_data">Audio Distribution (Song Metadata Import)</Option>
                  {formats.map(f => <Option key={`custom_${f.id}`} value={`custom_${f.id}`}>{f.name} (Custom)</Option>)}
                </Select>
              </div>

              <div>
                <Text strong>Company</Text>
                <Select value={companyId} onChange={setCompanyId} placeholder="Select Company" style={{ width: '100%', marginTop: 4 }}>
                  {companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
                </Select>
              </div>

              {uploadType !== 'song_data' && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Text strong>Month</Text>
                    <InputNumber value={month} onChange={setMonth} min={1} max={12} style={{ width: '100%', marginTop: 4 }} />
                  </Col>
                  <Col span={12}>
                    <Text strong>Year</Text>
                    <InputNumber value={year} onChange={setYear} min={2020} max={2030} style={{ width: '100%', marginTop: 4 }} />
                  </Col>
                </Row>
              )}

              <div>
                <Text strong>Excel/CSV File</Text>
                <Upload beforeUpload={(f) => { setFile(f); return false; }} maxCount={1} accept=".xlsx,.xls,.csv"
                  fileList={file ? [{ uid: '1', name: file.name, status: 'done' }] : []}>
                  <Button icon={<UploadOutlined />} style={{ marginTop: 4 }}>Select File</Button>
                </Upload>
              </div>

              {loading && (
                <Progress percent={progress} status={progress < 100 ? 'active' : 'success'} strokeColor="#217346" />
              )}

              <Button type="primary" onClick={handleUpload} loading={loading} block size="large"
                style={{ borderRadius: 8, background: '#217346' }}>
                {loading ? `Uploading... ${progress}%` : 'Upload & Process'}
              </Button>

            </Space>
          </Card>

          {/* Expected Format Guide */}
          <Card title="Expected Excel Columns" style={{ borderRadius: 12, marginTop: 16 }} size="small"
            extra={<Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setFormatModal(true)}>Add Format</Button>}>
            <Text strong>YouTube Revenue:</Text>
            <Paragraph code style={{ fontSize: 12 }}>Channel Name, Title/Video Title, Revenue/Amount/Earnings</Paragraph>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>* Columns can be in any order. Revenue column detects $ or ₹ automatically.</Text>

            <Text strong>Distribution Royalty:</Text>
            <Paragraph code style={{ fontSize: 12 }}>Song Title/Title, ISRC, UPC, Revenue/Royalty/Amount</Paragraph>

            <Text strong>Expense Data:</Text>
            <Paragraph code style={{ fontSize: 12 }}>Category/Type, Amount/Salary/Payment, Name/Payee, Description, Date</Paragraph>

            <Text strong>Channel Revenue (Auto-sync):</Text>
            <Paragraph code style={{ fontSize: 12 }}>Month, Channel, Channel Link, Subscribers, Videos, Views, Est. Revenue ($), Revenue (INR), RPM ($)</Paragraph>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>* Month column (e.g. "May 2026"). Revenue auto-updates in staff dashboards for assigned channels.</Text>

            <Text strong>Audio Distribution (Song Metadata):</Text>
            <Paragraph code style={{ fontSize: 12 }}>Artist, Release Title, Lebal Name, ISRC, UPC, Release, Dis.</Paragraph>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8 }}>* Songs that have an ISRC/UPC are treated as "Distributed" (not pending). If the same UPC/title appears again you'll get a warning — update or skip.</Text>

            {formats.length > 0 && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Text strong style={{ display: 'block', marginBottom: 8 }}>Custom Formats:</Text>
                {formats.map(f => (
                  <div key={f.id} style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <Text strong>{f.name}:</Text>
                      <Paragraph code style={{ fontSize: 12, marginBottom: 0 }}>{f.columns_list}</Paragraph>
                    </div>
                    <Popconfirm title="Delete this format?" description="All data uploaded using this format will also be deleted." onConfirm={() => handleDeleteFormat(f.id)} okText="Yes" cancelText="No" okButtonProps={{ danger: true }}>
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                ))}
              </>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          {result && (
            <Card title="Upload Result" style={{ borderRadius: 12, marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}><Statistic title="Total Rows" value={result.total || result.imported || 0} /></Col>
                <Col span={8}><Statistic title="Matched" value={result.matched || result.created || 0} valueStyle={{ color: '#52c41a' }} prefix={<CheckCircleOutlined />} /></Col>
                <Col span={8}><Statistic title="Unmatched" value={result.unmatched || 0} valueStyle={{ color: result.unmatched > 0 ? '#ff4d4f' : '#000' }} prefix={result.unmatched > 0 ? <CloseCircleOutlined /> : null} /></Col>
              </Row>
              {result.unmatched_rows && result.unmatched_rows.length > 0 && (
                <>
                  <Divider />
                  <Text strong>Unmatched Rows:</Text>
                  <Table
                    dataSource={result.unmatched_rows}
                    columns={Object.keys(result.unmatched_rows[0]).map(k => ({ title: k, dataIndex: k, key: k }))}
                    rowKey={(_, i) => i}
                    size="small"
                    pagination={false}
                    scroll={{ x: true }}
                    style={{ marginTop: 8 }}
                  />
                </>
              )}
            </Card>
          )}
        </Col>
      </Row>

      <Divider />

      <Card title="Upload History" style={{ borderRadius: 12 }}>
        <Table dataSource={uploads} columns={uploadColumns} rowKey="id" size="small" pagination={{ pageSize: 10 }} />
      </Card>

      <Modal title="Add New Excel Format" open={formatModal} onCancel={() => setFormatModal(false)} onOk={handleAddFormat} okText="Add Format">
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Text strong>Format Name</Text>
            <Input placeholder="e.g. Monthly Sales Report" value={newFormatName} onChange={e => setNewFormatName(e.target.value)} style={{ marginTop: 4 }} />
          </div>
          <div>
            <Text strong>Column Names (comma separated)</Text>
            <Input.TextArea placeholder="e.g. Month, Channel, Revenue, Views" value={newFormatColumns} onChange={e => setNewFormatColumns(e.target.value)} rows={3} style={{ marginTop: 4 }} />
            <Text type="secondary" style={{ fontSize: 11 }}>Enter the column names that your Excel/CSV file will have, separated by commas.</Text>
          </div>
        </Space>
      </Modal>
    </div>
  );
}

function Statistic({ title, value, valueStyle, prefix }) {
  return (
    <div>
      <Text type="secondary">{title}</Text>
      <div style={{ fontSize: 24, fontWeight: 'bold', ...valueStyle }}>
        {prefix} {value}
      </div>
    </div>
  );
}
